import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PlusCircle, BarChart3, Gift, Wallet as WalletIcon } from 'lucide-react'
import TabBar from '../components/TabBar'
import BalanceExpiryModal from '../components/BalanceExpiryModal'
import { useStore } from '../store/useStore'
import { useI18n } from '../i18n/useI18n'
import { formatHomeCurrency } from '../utils/currency'
import { api } from '../services/api'
import { isWeb3Mode } from '../lib/authMode'
import { isYokosoPointsEnabled } from '../lib/features'

/**
 * #1188 Phase 2: Wallet タブ。
 * ホームから残高・チャージ・履歴・失効内訳を分離し独立タブに集約
 * (PayPay/楽天ペイ 準拠、金融アプリの中心機能はここに集める)。
 */
export default function Wallet() {
  const navigate = useNavigate()
  const { t, locale } = useI18n()
  const { wallet, isLoggedIn, refreshChainBalance, setBalance } = useStore()

  const web3 = isWeb3Mode()
  const balanceJPYC = wallet?.balanceJPYC ?? 0
  const homeCurrencyText = formatHomeCurrency(balanceJPYC, locale)

  const [isLoadingBalance, setIsLoadingBalance] = useState(web3)
  const [expiryModalOpen, setExpiryModalOpen] = useState(false)
  const [nextExpiring, setNextExpiring] = useState<{ date: string; amount: number } | null>(null)
  // #1433 発行体ごとの残高。**発行体ごとに使える店が違う**ので、合計 1 つの数字には
  // 纏められない（「平塚市の分は平塚の加盟店だけ」を客が誤解しないように分けて出す）
  const [sponsorPoints, setSponsorPoints] = useState<
    Array<{ id: string; name: string; balance_jpy: number }>
  >([])

  // Web3 mode: on-chain 残高を mount 時に refresh (#1246: ゲストは wallet 無し)
  useEffect(() => {
    if (web3 && isLoggedIn) {
      refreshChainBalance().finally(() => setIsLoadingBalance(false))
    }
  }, [refreshChainBalance, web3, isLoggedIn])

  // #1764 支払い済みなのに未反映のチャージを拾う。振込画面を閉じた後に入金した分は
  // 誰も polling しないので、ウォレットを開いたときに Stripe と突き合わせて反映する。
  // 反映があれば残高を取り直す (Web3 mode は on-chain 残高なので対象外)。
  // cancelled 方式にしない: StrictMode の double-fire では 2 回目が必ず「反映なし」に
  // なるので、1 回目の反映結果を捨てると画面が古いまま残る (POST は冪等なので 2 回でも無害)。
  // `setBalance` は demo の debug API (reset-balance) を叩く手動設定用、`restoreSession`
  // は失敗するとログアウトさせるので、どちらも使わない。
  useEffect(() => {
    if (!isLoggedIn || web3) return
    api.reconcilePendingCharges()
      .then((res) => {
        if (res.credited.length === 0) return
        return useStore.getState().refreshBalance()
      })
      .catch(() => { /* 401 / 通信断は無視 (次回開いたときに再試行) */ })
  }, [isLoggedIn, web3])

  // 次回失効の要約を fetch (#1246: ゲストは残高が無いので fetch しない)
  // #1774 ポイントが無い環境では残高そのものが無いので呼ばない
  useEffect(() => {
    if (!isLoggedIn || !isYokosoPointsEnabled()) return
    let cancelled = false
    ;(async () => {
      try {
        const res = await api.getExpiryBreakdown()
        if (cancelled) return
        if (res.next_expiring_at && res.next_expiring_amount_jpy) {
          setNextExpiring({
            date: res.next_expiring_at,
            amount: res.next_expiring_amount_jpy,
          })
        }
        // #1774 失効した分は内訳側では落ちるのに、残高カードは /auth/session の
        // キャッシュを見ているので古いまま残る。実額と食い違ったら取り直す
        // (この GET の時点でサーバ側のキャッシュは直っている)。
        if (res.total_active_jpy !== useStore.getState().wallet?.balanceJPYC) {
          await useStore.getState().refreshBalance()
        }
      } catch {
        /* 401 等は無視 */
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isLoggedIn])

  useEffect(() => {
    if (!isLoggedIn || !isYokosoPointsEnabled()) return
    let cancelled = false
    api.getSponsorPoints()
      .then((res) => { if (!cancelled) setSponsorPoints(res) })
      .catch(() => { /* 未対応環境 / 401 は無視 */ })
    return () => { cancelled = true }
  }, [isLoggedIn])

  // Demo triple-tap: 隠し debug で残高直接設定 (既存 Home 相当)
  const isDemoMode = import.meta.env.VITE_DEMO_MODE === 'true'
  const tapTimestamps = useRef<number[]>([])
  const handleBalanceTap = useCallback(() => {
    // #1774 未リリースの環境では残高そのものが無いので、内訳を開かせない
    if (!isYokosoPointsEnabled()) return
    const now = Date.now()
    tapTimestamps.current.push(now)
    tapTimestamps.current = tapTimestamps.current.filter((t) => now - t < 1000)
    if (isDemoMode && tapTimestamps.current.length >= 3) {
      tapTimestamps.current = []
      const input = window.prompt('Set balance:')
      if (input !== null && input.trim() !== '') {
        const value = parseInt(input, 10)
        if (!isNaN(value)) setBalance(value)
      }
      return
    }
    setExpiryModalOpen(true)
  }, [setBalance, isDemoMode])

  // #1246: ゲスト版ウォレット。/login へ弾かずタブとして開き、この場で
  // 「何ができるようになるか」を伝えて登録導線を出す (行き来を分断しない)。
  if (!isLoggedIn) {
    return (
      <div className="relative min-h-screen bg-warm-white safe-top safe-bottom">
        <div className="pb-24">
          <div className="px-4 pt-6">
            <h1 className="font-display text-2xl font-bold text-text-primary">
              {t('wallet.title')}
            </h1>
            <p className="mt-1 font-body text-xs text-text-secondary">
              {isYokosoPointsEnabled() ? t('wallet.subtitle') : t('wallet.subtitleUnavailable')}
            </p>
          </div>
          <div className="mx-4 mt-4 flex flex-col items-center rounded-[20px] bg-white px-6 py-10 text-center shadow-card">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-navy/10">
              <WalletIcon size={26} className="text-navy" />
            </div>
            {/* #1774 ポイントが無い環境で「チャージできます」と案内しない */}
            <p className="mt-4 font-body text-sm leading-relaxed text-text-secondary">
              {isYokosoPointsEnabled() ? t('wallet.guestNotice') : t('wallet.guestNoticeUnavailable')}
            </p>
            <button
              onClick={() => {
                sessionStorage.setItem('postLoginRedirect', '/wallet')
                navigate('/login')
              }}
              className="tap-effect mt-5 w-full rounded-full bg-yokoso-red py-3 font-display text-sm font-semibold text-white"
            >
              {t('home.guestCta.title')}
            </button>
          </div>
        </div>
        <TabBar />
      </div>
    )
  }

  return (
    <div className="relative min-h-screen bg-warm-white safe-top safe-bottom">
      <div className="pb-24">
        <div className="px-4 pt-6">
          <h1 className="font-display text-2xl font-bold text-text-primary">
            {t('wallet.title')}
          </h1>
          <p className="mt-1 font-body text-xs text-text-secondary">
            {isYokosoPointsEnabled() ? t('wallet.subtitle') : t('wallet.subtitleUnavailable')}
          </p>
        </div>

        {/* Balance Card */}
        <div className="mt-4 px-4">
          <div
            onClick={handleBalanceTap}
            className="animate-scale-in relative h-[180px] w-full cursor-pointer select-none overflow-hidden rounded-[20px] shadow-lg"
            style={{
              background: 'linear-gradient(135deg, #1D3557 0%, #457B9D 70%, #E63946 130%)',
            }}
          >
            <div
              className="pointer-events-none absolute inset-0 opacity-[0.06]"
              style={{
                backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)',
                backgroundSize: '20px 20px',
              }}
            />
            <div className="relative z-10 flex h-full flex-col justify-between p-5">
              <div>
                <p className="font-display text-[13px] font-semibold tracking-wider text-white">
                  YOKOSO PAY
                </p>
                {isYokosoPointsEnabled() && (
                  <p className="mt-2 font-body text-[13px] text-white/70">
                    {t('home.balance')}
                  </p>
                )}
              </div>
              <div>
                {/* #1774 **本番に YOKOSO 通貨はまだ無い。** 残高 ¥0 と規約文を出すと
                   「買える通貨がある」と読めてしまうが、増やす手段は 1 つも無い。
                   カード自体は残して「準備中」と言い切る (デモは従来どおり残高を出す)。 */}
                {!isYokosoPointsEnabled() ? (
                  <>
                    <p className="font-display text-[22px] font-bold leading-none text-white">
                      {t('wallet.preparingTitle')}
                    </p>
                    <p className="mt-1.5 font-body text-[12px] text-white/70">
                      {t('wallet.preparingNote')}
                    </p>
                  </>
                ) : isLoadingBalance ? (
                  <>
                    <div className="h-[32px] w-[160px] rounded-lg bg-white/10 animate-pulse" />
                    <p className="mt-1.5 font-body text-[12px] text-white/50">
                      {t('home.balanceLoading')}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-display text-[32px] font-bold leading-none text-gold">
                      &yen; {balanceJPYC.toLocaleString()}
                    </p>
                    {homeCurrencyText && (
                      <p className="mt-1 font-body text-[13px] text-white/70">
                        {homeCurrencyText}
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* #1433 発行体ごとの残高。使える店が違うので分けて出す */}
        {sponsorPoints.length > 0 && (
          <div className="mx-4 mt-3 flex flex-col gap-2">
            {sponsorPoints.map((sp) => (
              <div
                key={sp.id}
                className="flex items-center justify-between rounded-xl border border-navy/15 bg-white px-4 py-3"
              >
                <div className="min-w-0">
                  <p className="font-body text-xs font-semibold text-text-primary">{sp.name}</p>
                  <p className="mt-0.5 font-body text-[10px] text-text-tertiary">
                    {t('wallet.sponsorScopeNote')}
                  </p>
                </div>
                <p className="shrink-0 font-display text-base font-bold text-navy">
                  &yen; {sp.balance_jpy.toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* 次回失効サマリ */}
        {nextExpiring && (
          <div
            onClick={() => setExpiryModalOpen(true)}
            className="tap-effect mx-4 mt-2 cursor-pointer rounded-xl border border-yellow-200 bg-yellow-50 px-4 py-2"
          >
            <div className="flex items-center justify-between">
              <span className="font-body text-xs text-yellow-800">
                {t('home.nextExpiring', {
                  amount: nextExpiring.amount.toLocaleString(),
                  date: nextExpiring.date,
                })}
              </span>
              <span className="font-body text-[10px] text-yellow-700">
                {t('home.tapForDetail')}
              </span>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mt-4 flex gap-3 px-4">
          {/* #1446 ポイントが無効な環境ではチャージへの導線を出さない。
             本番はポイント自体が存在しない（支払いの選択肢も出ない）のに、
             ここだけ開いていて**お金を払わずに残高を増やせる**状態だった。
             ルート側 (RequirePointsEnabled) と API 側でも塞いでいる。 */}
          {isYokosoPointsEnabled() && (
          <button
            onClick={() => navigate('/charge')}
            className="tap-effect flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-yokoso-red font-display text-[15px] font-semibold text-white shadow-button transition-colors hover:bg-yokoso-red-dark"
          >
            <PlusCircle size={20} />
            {t('home.charge')}
          </button>
          )}
          {isYokosoPointsEnabled() && (
          <button
            onClick={() => navigate('/points/redeem')}
            className="tap-effect flex h-12 flex-1 items-center justify-center gap-2 rounded-xl border border-[#E5E5E5] bg-white font-display text-[15px] font-semibold text-navy transition-colors hover:bg-gray-50"
          >
            <Gift size={20} />
            {t('wallet.redeemCode')}
          </button>
          )}
          <button
            onClick={() => navigate('/history')}
            className="tap-effect flex h-12 flex-1 items-center justify-center gap-2 rounded-xl border border-[#E5E5E5] bg-white font-display text-[15px] font-semibold text-navy transition-colors hover:bg-gray-50"
          >
            <BarChart3 size={20} />
            {t('home.history')}
          </button>
        </div>

        {/* Policy note — #1774 ポイントが無い環境では出さない (買えないものの規約) */}
        {isYokosoPointsEnabled() && (
        <div className="mx-4 mt-6 rounded-xl border border-gray-200 bg-white p-4">
          <p className="font-body text-xs font-semibold text-text-primary">
            {t('wallet.policyTitle')}
          </p>
          <p className="mt-1 font-body text-xs leading-5 text-text-secondary">
            {t('wallet.policyBody')}
          </p>
        </div>
        )}
      </div>

      <TabBar />

      <BalanceExpiryModal
        open={expiryModalOpen}
        onClose={() => setExpiryModalOpen(false)}
      />
    </div>
  )
}
